import type { CodeHistory } from '@yandex-int/k-common/typings';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUnmount } from 'react-use';
import { debounce } from 'lodash';
import { history } from '@codemirror/commands';
import { EditorView } from '@codemirror/view';

import Spin from '../../spin/spin';
import { useOnBeforeUnload, useStableCallback } from '../../../utils/hooks';
import { useCodingProblemProps } from '../context';
import { useCodingStore } from '../state';

import { CodePasteTracker } from './_lib/code-pastes';
import { LocalCodeSession, saveCodeSession, codeSessionFromLocal } from './_lib/solve-session';
import { CodeEditorMobile } from './_view/_mobile/code-editor_view_mobile';
import { CodeEditorDesktop } from './_view/_desktop/code-editor_view_desktop';
import { trackCodeSession } from './_lib/track-session-extension';
import { LazyCodeHistory, useInitialCodeHistory, useOnChangeHistory } from './_lib/use-code-history';

interface CodingCodeEditorProps {
    view?: 'mobile';
    initialValue: () => string | CodeHistory;
    sessionKey: string;
}

export function CodingCodeEditor({ view, initialValue, sessionKey }: CodingCodeEditorProps) {
    const isMobile = view === 'mobile';
    const props = useCodingProblemProps();
    const { isSolveSessionLoading, sendSolveSession } = props;
    const language = props.problem.markup.languages[0];
    const readOnly = props.readOnly === true;

    const skipInitialSetAnswer = useRef(true);
    const setAnswer = useStableCallback((value: string) => {
        props.setAnswer?.({
            markerAnswer: {
                user_answer: {
                    code: value,
                    language: language.language,
                },
            },
        });
    });
    const setAnswerDebounced = useMemo(() => debounce(setAnswer, 200), [setAnswer]);

    // Подтверждаем закрытие вкладки, если был обновлен код
    const [shouldConfirmExit, setShouldConfirmExit] = useState(false);
    useOnBeforeUnload(shouldConfirmExit);

    const setCode = useCodingStore((store) => store.setCode);

    const onChange = useCallback(
        (value: string) => {
            setCode(value);
            setShouldConfirmExit(true);
            if (skipInitialSetAnswer.current) {
                skipInitialSetAnswer.current = false;
                // чтобы кнопка Ответить не была сразу активной при заходе в задачу
                // ? можно еще использовать setValidator?
                return;
            }
            setAnswerDebounced(value);
        },
        [setAnswerDebounced, setCode]
    );

    const latestSolveSession = useRef<LocalCodeSession | undefined>();

    useEffect(() => {
        return useCodingStore.subscribe(
            () => {
                if (latestSolveSession.current && sendSolveSession) {
                    sendSolveSession(codeSessionFromLocal(latestSolveSession.current));
                }
            },
            (s) => s.submitCount
        );
    }, [sendSolveSession]);

    const onChangeHistory = useStableCallback((getter: LazyCodeHistory) => {
        const codeHistory = getter.getHistory();
        const session: LocalCodeSession = {
            text: codeHistory.lastText,
            history: codeHistory,
        };
        if (sessionKey) {
            saveCodeSession(sessionKey, session);
        }
        latestSolveSession.current = session;
    });

    const onChangeHistoryDebounced = useMemo(() => debounce(onChangeHistory, 200), [onChangeHistory]);
    useUnmount(() => onChangeHistoryDebounced.cancel());

    const [pasteTracker] = useState(() => new CodePasteTracker());
    const codeEditorExtensions = useMemo(
        () => [
            history({ minDepth: sendSolveSession ? Infinity : undefined }),
            // Включает перенос строки
            EditorView.lineWrapping,
            trackCodeSession(pasteTracker),
        ],
        [pasteTracker, sendSolveSession]
    );

    const [initialCode, initialState] = useInitialCodeHistory(initialValue, pasteTracker);
    const onCodeChange = useOnChangeHistory(pasteTracker, onChange, onChangeHistoryDebounced);

    if (isSolveSessionLoading) {
        return <Spin center delay />;
    }

    const Editor = isMobile ? CodeEditorMobile : CodeEditorDesktop;
    return (
        <Editor
            extensions={codeEditorExtensions}
            initialState={initialState}
            initialValue={initialCode}
            onChange={onCodeChange}
            readOnly={readOnly}
        />
    );
}
